import { type GridView, GridViewDialog } from "@appretio/shared";
import { IconButton } from "@appretio/ui-kit";
import { useState } from "react";

const KEEP_STATE = { replaceState: false };

/** The words an edit dialog uses for its list's kind of view. */
export interface EditViewTexts {
  /** Icon tooltip and dialog title, e.g. "Edit view". */
  title: string;
  /** Placeholder of the empty name field. */
  namePlaceholder: string;
  /** The refresh switch's label. */
  refreshLabel: string;
}

interface EditViewButtonProps {
  /** The applied saved view. ``undefined`` hides the button. */
  view?: GridView;
  /** What the dialog calls the view and its refresh. */
  texts: EditViewTexts;
  /** Names the user's other views hold — duplicates are rejected. */
  existingNames: readonly string[];
  /** Renames the view, and re-captures its state when asked. */
  onEdit: (name: string, replaceState: boolean) => Promise<unknown>;
  /** Whether the edit is in flight. */
  isPending: boolean;
}

/**
 * Pencil icon that renames the applied view and, on request, refreshes its
 * saved setup from what's on screen.
 */
export function EditViewButton({
  view,
  texts,
  existingNames,
  onEdit,
  isPending
}: EditViewButtonProps) {
  const [open, setOpen] = useState(false);

  if (!view) return null;

  return (
    <>
      <IconButton
        iconName="pencil"
        variant="tertiary"
        size="xs"
        onClick={() => setOpen(true)}
        title={texts.title}
        aria-label={texts.title}
      />
      <GridViewDialog
        open={open}
        onOpenChange={setOpen}
        title={texts.title}
        initialName={view.name}
        namePlaceholder={texts.namePlaceholder}
        existingNames={existingNames}
        toggles={[{ key: "replaceState", label: texts.refreshLabel }]}
        initialToggles={KEEP_STATE}
        isPending={isPending}
        onConfirm={(name, toggles) => onEdit(name, toggles.replaceState)}
      />
    </>
  );
}
